import { Component, type ReactNode, type ErrorInfo } from 'react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardFooter,
  Button,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ErrorCircle24Regular, Home24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import { reportReactError, logError } from '@shared/telemetry';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: '500px',
    width: '100%',
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '48px',
    marginBottom: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'center',
  },
  code: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});

// Router error boundary
export function ErrorBoundary() {
  const error = useRouteError();
  const styles = useStyles();

  let title = 'An error occurred';
  let message = 'Something went wrong. Please try again.';
  let details: string | undefined;

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 404:
        title = 'Page Not Found';
        message = 'The page you are looking for does not exist.';
        break;
      case 401:
        title = 'Unauthorized';
        message = 'You need to log in to access this page.';
        break;
      case 403:
        title = 'Access Denied';
        message = 'You do not have permission to access this page.';
        break;
      case 500:
        title = 'Server Error';
        message = 'An internal server error occurred.';
        break;
      default:
        title = `Error ${error.status}`;
        message = error.statusText || message;
    }
    // Report route errors to Faro
    logError(`Route error: ${error.status} ${error.statusText}`, {
      status: error.status,
      url: window.location.href,
    });
  } else if (error instanceof Error) {
    message = error.message;
    details = error.stack;
    // Report Error objects to Faro
    logError(error, { type: 'route_error', url: window.location.href });
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={<ErrorCircle24Regular className={styles.icon} />}
          header={<Title3>{title}</Title3>}
          description={<Text>{message}</Text>}
        />
        {details && import.meta.env.DEV && (
          <div style={{ padding: tokens.spacingHorizontalM }}>
            <pre className={styles.code}>{details}</pre>
          </div>
        )}
        <CardFooter>
          <div className={styles.actions}>
            <Button
              appearance="secondary"
              icon={<ArrowClockwise24Regular />}
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
            <Link to="/">
              <Button appearance="primary" icon={<Home24Regular />}>
                Home
              </Button>
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

// Class-based error boundary for component errors
interface ErrorBoundaryClassProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryClassState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryClassState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // Report to Faro with component stack trace
    reportReactError(error, errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card>
          <CardHeader
            image={<ErrorCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />}
            header={<Title3>Something went wrong</Title3>}
            description={<Text>{this.state.error?.message || 'An unexpected error occurred'}</Text>}
          />
          <CardFooter>
            <Button
              appearance="primary"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try again
            </Button>
          </CardFooter>
        </Card>
      );
    }

    return this.props.children;
  }
}
